import { useState, useEffect } from "react";
import { Pencil, Plus, RefreshCw } from "lucide-react";
import CategoryModal from "./CategoryModal";
import { BASE_URL } from "../Api/Config";

const StoreCategories = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get auth token from localStorage or wherever you store it
  const getAuthToken = () => {
    return localStorage.getItem('token') || 'your-auth-token';
  };

  const fetchStoresData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BASE_URL}/categories/admin/restaurantscategories`, {
        method: 'GET',
        headers: {
          'authorization': getAuthToken(),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        setStores(result.data.restaurants || []);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching stores data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStoresData();
    // eslint-disable-next-line
  }, []);

  const handleManageCategories = (store) => {
    setSelectedStore(store);
    setIsModalOpen(true);
  };

  const handleQuickAddCategory = (storeId) => {
    const store = stores.find((s) => s.id === storeId);
    if (store) {
      setSelectedStore(store);
      setIsModalOpen(true);
    }
  };

  const handleCategoriesUpdate = (storeId, categories) => {
    setStores(prev =>
      prev.map(store => store.id === storeId ? {...store, categories} : store)
    );
    
    alert("Categories updated\nStore categories have been successfully updated.");
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStore(null);
  };

  const handleRefresh = () => {
    fetchStoresData();
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="text-gray-600 text-lg">Loading stores...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="text-center py-12">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h2>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Manage Store Categories</h1>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {stores.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0h2m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h1m-1 4h1m4-4h1m-1 4h1"></path>
              </svg>
            </div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">No Restaurants Found</h2>
            <p className="text-gray-500">No restaurants are currently available to manage.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto bg-white rounded-lg shadow p-4">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold">
                  <tr>
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Store Name</th>
                    <th className="px-4 py-2">Categories</th>
                    <th className="px-4 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stores.map((store, index) => (
                    <tr key={store.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-gray-500">{(index + 1).toString().padStart(2,'0')}</td>
                      <td className="px-4 py-2 text-gray-900 font-medium">{store.name}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-gray-200 rounded text-gray-800 text-xs font-medium">
                            {store.categories?.length || 0} categories
                          </span>
                          {store.categories?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {store.categories.slice(0, 2).map((cat, index) => (
                                <span key={cat.id || `cat-${index}`} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                  {cat.name}
                                </span>
                              ))}
                              {store.categories.length > 2 && (
                                <span key="more-categories" className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                  +{store.categories.length - 2} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex gap-2 justify-center">
                          <button 
                            onClick={() => handleManageCategories(store)} 
                            className="flex items-center gap-1 px-3 py-1 bg-blue-50 border border-blue-200 rounded text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            <Pencil className="w-4 h-4"/> Manage
                          </button>
                          <button 
                            onClick={() => handleQuickAddCategory(store.id)} 
                            className="flex items-center gap-1 px-3 py-1 bg-green-50 border border-green-200 rounded text-green-600 hover:bg-green-100 transition-colors"
                          >
                            <Plus className="w-4 h-4"/> Add
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="sm:hidden space-y-4">
              {stores.map((store, index) => (
                <div key={store.id} className="bg-white rounded-xl border shadow-sm p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h2 className="font-semibold text-gray-900">{store.name}</h2>
                      <p className="text-sm text-gray-500">ID: #{(index + 1).toString().padStart(3,'0')}</p>
                    </div>
                    <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded text-xs font-medium">
                      {store.categories?.length || 0} categories
                    </span>
                  </div>
                  
                  {/* Categories Preview */}
                  {store.categories?.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {store.categories.slice(0, 3).map((cat, index) => (
                          <span key={cat.id || `mobile-cat-${index}`} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                            {cat.name}
                          </span>
                        ))}
                        {store.categories.length > 3 && (
                          <span key="mobile-more-categories" className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                            +{store.categories.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleManageCategories(store)} 
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-blue-600 hover:bg-blue-100 transition-colors"
                    >
                      <Pencil className="w-4 h-4"/> Manage
                    </button>
                    <button 
                      onClick={() => handleQuickAddCategory(store.id)} 
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-50 border border-green-200 rounded text-green-600 hover:bg-green-100 transition-colors"
                    >
                      <Plus className="w-4 h-4"/> Add
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <CategoryModal
        store={selectedStore}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onCategoriesUpdate={handleCategoriesUpdate}
        onRefresh={fetchStoresData}
      />
    </div>
  );
};

export default StoreCategories;